// "Peek lifecycle" subsection of the peek-readiness probe. Extracted
// from `DevPanelActivityDockPeekReadinessProbe.tsx` to keep the probe
// under the 400-line hard hygiene cap (it crossed when this row +
// its subscription + the long explainer copy landed in-place).
//
// What this row owns:
//   - A live subscription to the module-level peeked-modes store
//     (`src/lib/activityDock/peekedModesStore.ts`) so the dev sees
//     exactly which modes the dock has already dedup'd this session.
//   - A "Clear peeked-modes" button that calls `clearPeekedModes()`
//     so a developer can re-trigger a peek without a tab reload
//     (the previous workflow).
//   - The doctrinal explainer copy about the three gates the dock
//     applies BEYOND the projection above, plus the doctrinal carve-
//     out for the `ACTIVITY_DOCK_OPEN_EVENT` channel (active-lead
//     checklist auto-opens that don't emit peek events on purpose).
//
// State is owned here (a generation counter on each subscribe
// notification) rather than lifted to the probe, because nothing
// else in the probe needs to know about peek-session dedup. Keeps
// the probe focused on the projection model.

import { useEffect, useMemo, useState } from "react";
import {
  clearPeekedModes,
  getPeekedModes,
  subscribePeekedModes,
} from "@/lib/activityDock/peekedModesStore";

export function PeekProbeLifecycleRow() {
  // The store reuses the same Set reference on every notification,
  // so we trigger re-renders via a generation counter instead of a
  // referentially-aware snapshot. `peekedModesList` is then recomputed
  // by reading the store directly at render time.
  const [peekedModesGen, setPeekedModesGen] = useState(0);
  useEffect(() => {
    return subscribePeekedModes(() => setPeekedModesGen((g) => g + 1));
  }, []);
  const peekedModesList = useMemo(() => {
    void peekedModesGen;
    return Array.from(getPeekedModes()).sort();
  }, [peekedModesGen]);

  return (
    <div className="mt-3 rounded-lg border border-sky-200 bg-sky-50/50 p-2 text-[11px] dark:border-sky-700/60 dark:bg-sky-900/15">
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <span className="font-semibold uppercase tracking-wide text-sky-900 dark:text-sky-200">
          Peek lifecycle (session dedup)
        </span>
        <button
          type="button"
          onClick={clearPeekedModes}
          disabled={peekedModesList.length === 0}
          className="rounded border border-sky-300 bg-white px-2 py-0.5 text-sky-900 disabled:opacity-50 dark:border-sky-700 dark:bg-zinc-800 dark:text-sky-200"
        >
          Clear peeked-modes
        </button>
      </div>
      <div className="text-gray-800 dark:text-zinc-200">
        Peeked this session:{" "}
        {peekedModesList.length === 0 ? (
          <span className="text-gray-500 dark:text-zinc-400">
            (none — next mode transition with a peek-worthy signal will
            fire)
          </span>
        ) : (
          <span className="font-mono">{peekedModesList.join(", ")}</span>
        )}
      </div>
      <p className="mt-1 text-[10px] text-sky-800 dark:text-sky-300">
        The real dock applies three gates BEYOND the projection above:
        (1) requires an actual mode TRANSITION since this tab opened
        (initial-mount never peeks), (2) at most one peek per mode per
        tab session — listed above; click Clear to reset without a tab
        reload, (3) assist level must allow peeks. The active-lead
        checklist opens the dock via a separate{" "}
        <span className="font-mono">ACTIVITY_DOCK_OPEN_EVENT</span>{" "}
        channel that intentionally doesn't emit a peek event (avoids
        double-firing on lead-id flips); seeing the dock pop out for
        an outstanding-items lead with no peek recorded is that
        channel, not a missed peek.
      </p>
    </div>
  );
}
